import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  DialogChromeFooter,
  DialogChromeHeader,
  dialogActionButtonSx,
  dialogPrimaryActionSx,
} from 'components/atoms/Dialog/DialogChrome';
import { DropdownOption } from 'components/atoms/Dropdown/Dropdown';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import { find, includes, isEqual, keys, map } from 'es-toolkit/compat';

import React, { useEffect, useMemo, useState } from 'react';
import { useChannelsOptionsWithAll } from 'utils/queryHooks/useChannelsOptions';
import { useCasesParams } from 'utils/useCasesParams';
import useStudy from 'utils/useStudy';
import useStudyMutations from 'utils/useStudyMutations';
import { getSelectedChannels } from '../ProceduresPage/Actions/Multiplex/helpers';
import { DefineMultiplexPanelsDialog } from '../ProceduresPage/Actions/DefineMultiplexPanelsDialog';
import LegendQCImpact from './LegendQCImpact';
import QCMarkerBarcodeChart from './QCMarkerBarcodeChart';
import QCMarkerNormalizationInsights from './QCMarkerNormalizationInsights';
import QCMarkerSettingsTable from './QCMarkerSettingsTable';

interface QCMarkersProps {
  studyId: string;
}

const getDefinedQcMarkerSettings = (qcMarkersSettings: { [markerId: string]: string } = {}) => {
  const cleanedQcMarkerSettings: { [markerId: string]: string } = {};

  map(keys(qcMarkersSettings), (markerId) => {
    const value = qcMarkersSettings[markerId];

    if (value) {
      cleanedQcMarkerSettings[markerId] = value;
    }
  });

  return cleanedQcMarkerSettings;
};

const QCMarkers: React.FC<QCMarkersProps> = ({ studyId }) => {
  const isSpecificStudySelected = studyId && !includes(NON_SPECIFIC_STUDY_VALUES, studyId.toString());
  const { study } = useStudy(studyId, { enabled: isSpecificStudySelected });
  const { studyMutation } = useStudyMutations(study);

  const qcMarkersStudySettings = useMemo(
    () => getDefinedQcMarkerSettings(study?.settings?.qcMarkersSettings || {}),
    [study?.settings?.qcMarkersSettings]
  );
  const [configuredMarkerIds, setConfiguredMarkerIds] = useState<string[]>([]);
  const [qcMarkersSettings, setQcMarkersSettings] = useState<{
    [markerId: string]: string;
  }>({});
  const [initializedStudyId, setInitializedStudyId] = useState<string | null>(null);

  const hasHydratedDraft = !study?.id || initializedStudyId === study.id;
  const hasUnsavedConfiguredMarkerChanges =
    hasHydratedDraft && !isEqual(configuredMarkerIds, keys(qcMarkersStudySettings));
  const [barcodeScope, setBarcodeScope] = useState<'dataset' | 'study'>('study');
  const [isPanelSetupOpen, setIsPanelSetupOpen] = useState(false);
  const [showGlobalImpact, setShowGlobalImpact] = useState(true);
  const [barcodeColorBy, setBarcodeColorBy] = useState<'impact' | 'label'>('impact');
  const [isCohortMarkerQcEditorOpen, setIsCohortMarkerQcEditorOpen] = useState(false);
  const [draftConfiguredMarkerIds, setDraftConfiguredMarkerIds] = useState<string[]>([]);
  const [draftQcMarkersSettings, setDraftQcMarkersSettings] = useState<{ [markerId: string]: string }>({});
  const cleanedDraftQcMarkersSettings = useMemo(
    () => getDefinedQcMarkerSettings(draftQcMarkersSettings),
    [draftQcMarkersSettings]
  );
  const hasUnsavedCohortLevelMarkerQcChanges = !isEqual(cleanedDraftQcMarkersSettings, qcMarkersStudySettings);

  useEffect(() => {
    if (!study?.id) {
      return;
    }

    const isStudyChanged = initializedStudyId !== study.id;

    if (isStudyChanged || !hasUnsavedConfiguredMarkerChanges) {
      const nextConfiguredMarkerIds = keys(qcMarkersStudySettings);

      if (!isEqual(configuredMarkerIds, nextConfiguredMarkerIds)) {
        setConfiguredMarkerIds(nextConfiguredMarkerIds);
      }

      if (!isEqual(qcMarkersSettings, qcMarkersStudySettings)) {
        setQcMarkersSettings(qcMarkersStudySettings);
      }

      if (isStudyChanged) {
        setInitializedStudyId(study.id);
      }
    }
  }, [
    configuredMarkerIds,
    hasUnsavedConfiguredMarkerChanges,
    initializedStudyId,
    qcMarkersSettings,
    qcMarkersStudySettings,
    study?.id,
  ]);

  const { casesParams } = useCasesParams();
  const { channelsOptionsWithAllOption, channelMarkersOptions, isLoadingChannelMarkers } = useChannelsOptionsWithAll({
    casesParams,
    currentChannels: configuredMarkerIds,
  });
  const markerIdsForNormalizationInsights = useMemo(
    () => map(channelMarkersOptions, ({ value }) => String(value)),
    [channelMarkersOptions]
  );
  const markerIdsForBarcodeChart = useMemo(() => keys(cleanedDraftQcMarkersSettings), [cleanedDraftQcMarkersSettings]);
  const applyDraftConfiguredMarkerIds = React.useCallback((nextConfiguredMarkerIds: string[]) => {
    setDraftConfiguredMarkerIds(nextConfiguredMarkerIds);
    setDraftQcMarkersSettings((previousSettings) => {
      const nextQcMarkersSettings = { ...previousSettings };
      map(keys(nextQcMarkersSettings), (markerId) => {
        if (!includes(nextConfiguredMarkerIds, markerId)) {
          delete nextQcMarkersSettings[markerId];
        }
      });

      return nextQcMarkersSettings;
    });
  }, []);

  const closeCohortMarkerQcEditor = React.useCallback(() => {
    if (studyMutation.isLoading) {
      return;
    }

    setIsCohortMarkerQcEditorOpen(false);
  }, [studyMutation.isLoading]);

  const openCohortMarkerQcEditor = React.useCallback(() => {
    setDraftConfiguredMarkerIds(configuredMarkerIds);
    setDraftQcMarkersSettings(qcMarkersSettings);
    setIsCohortMarkerQcEditorOpen(true);
  }, [configuredMarkerIds, qcMarkersSettings]);

  const handleConfiguredMarkersChange = React.useCallback(
    (_event: React.SyntheticEvent, newValue: DropdownOption[]) => {
      applyDraftConfiguredMarkerIds(getSelectedChannels(newValue, channelMarkersOptions));
    },
    [applyDraftConfiguredMarkerIds, channelMarkersOptions]
  );

  return (
    <Box
      sx={{
        pt: 3,
        px: { xs: 2, md: 6, lg: 10 },
      }}
    >
      <Stack spacing={3}>
        <Box data-testid="qc-marker-summary-sections">
          <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
            <Box display="flex" flexWrap="wrap" alignItems="center" justifyContent="space-between" gap={2}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  Slide-Level Marker QC
                </Typography>
                <Typography color="text.secondary">
                  Switch between the full study slide set and the current dataset tab filters when loading the summary
                  views and barcode drilldown.
                </Typography>
              </Box>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={barcodeScope}
                onChange={(_, nextScope) => {
                  if (nextScope) {
                    setBarcodeScope(nextScope);
                  }
                }}
                aria-label="Slide marker qc scope"
              >
                <ToggleButton value="study" aria-label="Entire study scope">
                  Entire study
                </ToggleButton>
                <ToggleButton value="dataset" aria-label="Dataset filters scope">
                  Dataset filters
                </ToggleButton>
              </ToggleButtonGroup>
              <Button variant="outlined" color="secondary" onClick={() => setIsPanelSetupOpen(true)}>
                Define multiplex panels
              </Button>
            </Box>
          </Paper>
          {isPanelSetupOpen && (
            <DefineMultiplexPanelsDialog
              casesParams={
                barcodeScope === 'study' ? { studyId, labId: casesParams.labId, filters: { studyId } } : casesParams
              }
              onClose={() => setIsPanelSetupOpen(false)}
            />
          )}
          <QCMarkerBarcodeChart
            studyId={studyId}
            qcMarkersSettings={qcMarkersSettings}
            markerIds={markerIdsForBarcodeChart}
            scope={barcodeScope}
            renderMode="summary"
            showGlobalImpact={showGlobalImpact}
            onShowGlobalImpactChange={setShowGlobalImpact}
          />
        </Box>

        <Paper sx={{ p: { xs: 2, md: 3 } }} variant="outlined" data-testid="qc-marker-global-config-section">
          <Stack spacing={2.5}>
            <Stack spacing={1}>
              <Typography variant="h4">Cohort-Level Marker QC</Typography>
              <Typography color="text.secondary" variant="body2" sx={{ maxWidth: 780 }}>
                Use this section to set the cohort-level marker QC impact for each marker. These defaults sit between
                the slide-level summaries above and the drilldown sections below.
              </Typography>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              justifyContent="flex-end"
              alignItems={{ sm: 'center' }}
            >
              <Typography color="text.secondary" variant="body2" sx={{ mr: { sm: 'auto' } }}>
                {configuredMarkerIds.length} markers configured
              </Typography>
              <Button variant="contained" size="small" onClick={openCohortMarkerQcEditor}>
                Edit cohort-level marker QC
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Dialog
          open={isCohortMarkerQcEditorOpen}
          onClose={closeCohortMarkerQcEditor}
          aria-labelledby="cohort-marker-qc-editor-title"
          maxWidth="md"
          fullWidth
          scroll="paper"
        >
          <DialogChromeHeader
            title="Edit Cohort-Level Marker QC"
            subtitle="Choose markers, then assign the cohort-level impact for each marker."
            titleId="cohort-marker-qc-editor-title"
            onClose={closeCohortMarkerQcEditor}
            sticky
          />
          <DialogContent dividers>
            <Stack spacing={2.5}>
              <Autocomplete
                multiple
                size="small"
                options={channelsOptionsWithAllOption}
                value={map(
                  draftConfiguredMarkerIds,
                  (channelId) =>
                    find(channelsOptionsWithAllOption, { value: channelId }) || {
                      value: channelId,
                      text: channelId,
                    }
                )}
                getOptionLabel={(option) => option.text}
                onChange={handleConfiguredMarkersChange}
                disableCloseOnSelect
                filterSelectedOptions
                isOptionEqualToValue={(option, val) => option.value === val.value}
                renderOption={(props, option, { selected }) => {
                  return (
                    <li {...props} key={option.value}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon />}
                        checkedIcon={<CheckBoxIcon />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                        size="small"
                      />
                      {option.text}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add markers to cohort-level marker QC"
                    placeholder="Search markers to add"
                    helperText="Use search to add markers or remove them directly from the selected list."
                    disabled={isLoadingChannelMarkers}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {isLoadingChannelMarkers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                limitTags={6}
              />

              {draftConfiguredMarkerIds.length ? (
                <QCMarkerSettingsTable
                  qcMarkers={draftConfiguredMarkerIds}
                  onMarkerValueChange={(markerId: string, value: string) => {
                    setDraftQcMarkersSettings((prevSettings) => ({ ...prevSettings, [markerId]: value }));
                  }}
                  qcMarkersSettings={draftQcMarkersSettings}
                />
              ) : (
                <Box
                  sx={{
                    px: 2,
                    py: 2.25,
                    borderRadius: 2,
                    border: (theme) => `1px dashed ${theme.palette.divider}`,
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography color="text.secondary" variant="body2">
                    No cohort-level marker QC configured yet. Add markers above, then choose whether each marker is
                    usable, flagged, or excluded.
                  </Typography>
                </Box>
              )}

              <Divider />
              <LegendQCImpact />
            </Stack>
          </DialogContent>
          <DialogChromeFooter sticky>
            {hasUnsavedCohortLevelMarkerQcChanges ? (
              <Chip label="Unsaved cohort-level marker QC changes" color="warning" size="small" sx={{ mr: 'auto' }} />
            ) : null}
            <Button
              variant="text"
              disabled={studyMutation.isLoading}
              onClick={closeCohortMarkerQcEditor}
              sx={dialogActionButtonSx}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={studyMutation.isLoading || !hasUnsavedCohortLevelMarkerQcChanges || !study?.id}
              onClick={() => {
                studyMutation.mutate({
                  id: study.id,
                  settings: {
                    ...study?.settings,
                    qcMarkersSettings: cleanedDraftQcMarkersSettings,
                  },
                });
              }}
              sx={dialogPrimaryActionSx}
            >
              {studyMutation.isLoading ? 'Saving...' : 'Save cohort-level marker QC'}
            </Button>
          </DialogChromeFooter>
        </Dialog>

        <Box data-testid="qc-marker-drilldown-section">
          <QCMarkerBarcodeChart
            studyId={studyId}
            qcMarkersSettings={qcMarkersSettings}
            markerIds={markerIdsForBarcodeChart}
            scope={barcodeScope}
            renderMode="barcode"
            showGlobalImpact={showGlobalImpact}
            onShowGlobalImpactChange={setShowGlobalImpact}
            barcodeColorBy={barcodeColorBy}
            onBarcodeColorByChange={setBarcodeColorBy}
          />
          <QCMarkerNormalizationInsights
            studyId={studyId}
            markerIds={markerIdsForNormalizationInsights}
            scope={barcodeScope}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default QCMarkers;
